import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSuperChatContentStore } from "@/store/useSuperChatContentStore";
import Router from "@/constants/router";

// 타입 정의
interface SuperChatContentFilter {
  category: string;
  search: string;
  useYn: "" | "Y" | "N";
}

/**
 * QNA 컨텐츠 관리 목록 페이지 커스텀 훅
 *
 * @description 도장 목록 useList 훅을 참고하여 구현
 * @returns QNA 컨텐츠 관리 목록 페이지 커스텀 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
function useSuperChatContentList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    contents,
    loading,
    error,
    filters,
    pagination,
    selectedIds,
    fetchContents,

    bulkUpdateContents,
    setFilters,
    setPagination,
    setSelectedIds,
  } = useSuperChatContentStore();

  // 로컬 상태
  const [isInitialized, setIsInitialized] = useState(false);

  // 기본 pagination 값
  const defaultPagination = {
    page: 1,
    limit: 10,
    pageSize: 10,
    total: 0,
    size: 10,
  };

  const currentPagination = pagination || defaultPagination;

  /**
   * 초기 데이터 로드
   */
  const initializeData = useCallback(async () => {
    if (!isInitialized) {
      try {
        // URL 파라미터에서 초기 필터 설정
        const useYnParam = searchParams.get("useYn");
        const initialFilters = {
          category: searchParams.get("category") || "",
          search: searchParams.get("search") || "",
          useYn:
            useYnParam === "Y" || useYnParam === "N"
              ? useYnParam
              : ("" as "" | "Y" | "N"),
        };

        const initialPage = parseInt(searchParams.get("page") || "1");
        const initialLimit = parseInt(searchParams.get("limit") || "10");

        // 필터와 페이지네이션 설정
        setFilters(initialFilters);
        setPagination({
          page: initialPage,
          limit: initialLimit,
          pageSize: initialLimit,
        });

        await fetchContents();
        setIsInitialized(true);
      } catch (error) {
        console.error("초기 데이터 로드 실패:", error);
      }
    }
  }, [isInitialized, searchParams, setFilters, setPagination, fetchContents]);

  /**
   * 검색 처리
   */
  const handleSearch = useCallback(
    (searchFilters: SuperChatContentFilter) => {
      setFilters(searchFilters);
      setPagination({ page: 1 }); // 검색 시 첫 페이지로
    },
    [setFilters, setPagination]
  );

  /**
   * 필터 처리
   */
  const handleFilter = useCallback(
    (filterValues: SuperChatContentFilter) => {
      setFilters(filterValues);
      setPagination({ page: 1 }); // 필터 변경 시 첫 페이지로
    },
    [setFilters, setPagination]
  );

  /**
   * 벌크 액션 처리
   */
  const handleBulkAction = useCallback(
    async (action: string, ids: number[]) => {
      try {
        await bulkUpdateContents(action, ids);
        // 성공 후 선택 해제
        setSelectedIds([]);
      } catch (error) {
        console.error("벌크 액션 실패:", error);
      }
    },
    [bulkUpdateContents, setSelectedIds]
  );

  /**
   * 컨텐츠 생성
   */
  const handleCreate = useCallback(() => {
    navigate(Router.SuperChatContentCreateContent);
  }, [navigate]);

  /**
   * 컨텐츠 수정
   */
  const handleEdit = useCallback(
    (contentId: number) => {
      navigate(
        Router.SuperChatContentDetail.replace(":id", contentId.toString())
      );
    },
    [navigate]
  );

  // 컴포넌트 마운트 시 초기화
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // 필터나 페이지네이션 변경 시 데이터 재로드
  useEffect(() => {
    if (isInitialized) {
      fetchContents();
    }
  }, [
    filters,
    currentPagination.page,
    currentPagination.limit,
    isInitialized,
    fetchContents,
  ]);

  // 에러 처리
  useEffect(() => {
    if (error) {
      console.error("QNA 컨텐츠 에러:", error);
      // 필요시 토스트 메시지 표시
    }
  }, [error]);

  /**
   * 페이지 변경 핸들러
   */
  const handlePageChange = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      setPagination({ page: page + 1 }); // MUI는 0-based, API는 1-based
    },
    [setPagination]
  );

  /**
   * 페이지당 항목 수 변경 핸들러
   */
  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setPagination({
        page: 1, // 페이지 크기 변경 시 첫 페이지로
        limit: newRowsPerPage,
        pageSize: newRowsPerPage,
      });
    },
    [setPagination]
  );

  return {
    // 데이터
    list: contents || [],
    isLoading: loading,
    page: currentPagination.page,
    count: currentPagination.total || 0,
    filters,
    selectedIds,
    rowsPerPage: currentPagination.limit || 10,

    // 핸들러
    handleSearch,
    handleFilter,
    handleBulkAction,
    handleCreate,
    handleEdit,
    handlePageChange,
    handleRowsPerPageChange,
  };
}

export default useSuperChatContentList;
